import React from 'react';
import SectionWrapper from '../ui/SectionWrapper';
import { FiExternalLink, FiLock, FiSmartphone, FiGlobe, FiShield, FiZap, FiDatabase } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const ProjectRow = ({ name, url, role, description, outcome, tags, icon }) => (
  <div className="group relative bg-white p-8 rounded-2xl border border-slate-200 hover:border-teal-200 transition-all hover:shadow-lg mb-8 last:mb-0">
    <div className="grid md:grid-cols-12 gap-8">
      <div className="md:col-span-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-teal-50 rounded-lg text-teal-600">
            <SafeIcon icon={icon} className="text-xl" />
          </div>
          <h3 className="text-2xl font-bold text-slate-900 group-hover:text-teal-700 transition-colors">
            {name}
          </h3>
        </div>
        <p className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-4">{role}</p>
        <div className="flex flex-wrap gap-2 mb-6">
          {tags.map(tag => (
            <span key={tag} className="px-2 py-1 bg-slate-50 text-slate-600 text-xs rounded border border-slate-100">
              {tag}
            </span>
          ))}
        </div>
        {url && (
          <a 
            href={`https://${url}`} 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center text-sm font-semibold text-teal-600 hover:text-teal-800"
          >
            Visit Website <SafeIcon icon={FiExternalLink} className="ml-1.5" />
          </a>
        )}
      </div>
      <div className="md:col-span-8 space-y-4">
        <div>
          <h4 className="text-sm font-bold text-slate-900 uppercase mb-1">The Challenge</h4>
          <p className="text-slate-600 leading-relaxed">{description}</p>
        </div>
        <div className="pt-2">
          <h4 className="text-sm font-bold text-slate-900 uppercase mb-1">Impact & Contribution</h4>
          <p className="text-slate-600 leading-relaxed">{outcome}</p>
        </div>
      </div>
    </div>
  </div>
);

const Projects = () => {
  const projects = [
    {
      name: "TakedownTaskforce.com",
      url: "takedowntaskforce.com",
      role: "Strategic Advisor & Lead",
      tags: ["Anti-Deepfake", "Legal Tech", "Security"],
      icon: FiShield,
      description: "Addressing the rapid rise of digital impersonation and deepfake attacks which outpace traditional legal remedies.",
      outcome: "Architected a rapid-response takedown protocol combining automated detection with legal enforcement, significantly reducing exposure for high-profile clients."
    },
    {
      name: "RescueFoodies.com",
      url: "rescuefoodies.com",
      role: "Platform Architect",
      tags: ["Logistics", "Sustainability", "Data"],
      icon: FiDatabase,
      description: "Solving logistical inefficiencies in food rescue operations to connect donors and volunteers securely in real-time.",
      outcome: "Designed the core operational data model and secure identity verification system for volunteers, optimizing delivery efficiency for perishable goods."
    },
    {
      name: "BoostAutomate.com",
      url: "boostautomate.com",
      role: "Consultant - Automation Strategy",
      tags: ["SaaS", "Workflow", "Automation"],
      icon: FiZap,
      description: "Helping small businesses adopt enterprise-grade automation without compromising customer data security.",
      outcome: "Created security-first automation frameworks that allow safe data orchestration across CRM and marketing platforms, enabling secure business scaling."
    },
    {
      name: "Security Bank",
      role: "Lead Architect (Retail App)",
      tags: ["Mobile Banking", "FinTech", "UX Architecture"],
      icon: FiSmartphone,
      description: "Developing a next-generation retail mobile application to compete in a digital-first banking market, requiring a balance of fluid UX and high-tier security.",
      outcome: "Led the development and building of the new retail app, integrating advanced security protocols with a modern user journey that serves millions of customers."
    },
    {
      name: "BDO Unibank",
      role: "Platform Manager (Biometrics)",
      tags: ["Identity", "Biometrics", "Enterprise Security"],
      icon: FiLock,
      description: "Managing the enterprise biometrics platform for the Philippines' largest bank to secure multi-channel transactions and prevent identity fraud.",
      outcome: "Established and managed a centralized identity verification system that significantly reduced fraud incidents while improving customer authentication speeds across the bank's massive network."
    },
    {
      name: "Interpol",
      role: "Regional Project Manager",
      tags: ["Global Security", "Cybercrime", "Law Enforcement"],
      icon: FiGlobe,
      description: "Overseeing regional initiatives to combat cross-border digital threats and enhance international cybercrime cooperation through Interpol's network.",
      outcome: "Managed complex multi-national security projects, coordinating technical and operational resources to strengthen regional defenses against emerging cyber threats."
    }
  ];

  return (
    <SectionWrapper id="projects" className="bg-slate-50">
      <div className="max-w-4xl mx-auto">
        <div className="mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Selected Work</h2>
          <p className="text-lg text-slate-600">
            A track record of leadership in cybersecurity, banking technology, and global security operations.
          </p>
        </div>
        <div>
          {projects.map((project, i) => (
            <ProjectRow key={i} {...project} />
          ))}
        </div>
      </div>
    </SectionWrapper>
  );
};

export default Projects;